/**
 * feature/manager — the Data Manager page (spec §F9; M6 §5; client, 2026-09-18).
 *
 * Seven acts over one page, each behind a button on the side rail: upload something new,
 * replace the data of something published, edit that data in place, correct a dataset's
 * metadata, report a manual indicator's values, work the review queue, manage the
 * published catalogue.
 *
 * **The rail replaced a three-tab strip, and then moved to the side** (client, 2026-09-19).
 * Three underlined words could not carry six acts —
 * "Upload" and "Update/Replace" are one word apart and a world apart in consequence — so
 * each button now says what it does, shows a glyph for it, and carries a line of hint under
 * the label. The look is the client's *"3D crystal"*: a glass gradient from the panel colour
 * into a tenth of the brand green, a one-pixel highlight along the top inside edge, a soft
 * drop shadow under it, and a two-pixel lift on hover. The active one is pressed *into* the
 * page instead — inset shadow, brand-tinted ground, brand text, an accent bar along the top.
 * Both the lift and the press are `transform`/`box-shadow` only, and both are turned off
 * under `prefers-reduced-motion`. The rail stands down the left of the page and stays put
 * while the panel beside it scrolls; below 900 px it lies down into one scrollable row above
 * the panel, because a column of seven tall buttons would push the work off a phone screen.
 *
 * It is still a real `tablist` with a roving tabindex — the same pattern as the catalogue
 * record's About/Data tabs — and each button keeps its old `mgr-tab-<key>` test id so every
 * test and e2e written against the strip still lands on the same element; the new
 * `mgr-rail-<key>` id is on the button's own content, which is what a click hits either way.
 *
 * The page owns two pieces of cross-panel state: the "New version of…" handoff, where a
 * click in Datasets opens **Replace** with that dataset preset, and which button is on.
 */
import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../../lib/icons';
import { DatasetsTab } from './DatasetsTab';
import { EditDataPanel } from './EditDataPanel';
import { IntelSettings } from './IntelSettings';
import { MetadataPanel } from './MetadataPanel';
import { Queue } from './Queue';
import { UploadWizard } from './UploadWizard';
import { ValuesPanel } from './values/ValuesPanel';

type Tab = 'upload' | 'replace' | 'edit' | 'metadata' | 'values' | 'queue' | 'datasets' | 'intel';

/**
 * Eight acts, in the order they are done rather than the order they were built. `values`
 * sits beside the other three ways data reaches the portal and before the queue that
 * decides on all of them — a manual indicator submission is an upload like any other,
 * it just makes its own file. `intel` (Task 6, design §6.5) is last: it edits the guided
 * analyses' own thresholds rather than the catalogue the other seven acts all touch.
 */
const TABS: Tab[] = ['upload', 'replace', 'edit', 'metadata', 'values', 'queue', 'datasets', 'intel'];

/** One glyph per act, from the portal's own 24×24 stroke set. */
const TAB_ICON: Record<Tab, IconName> = {
  upload: 'upload',
  replace: 'sync',
  edit: 'pencil',
  metadata: 'tag',
  values: 'chart',
  queue: 'inbox',
  datasets: 'db',
  intel: 'cpu',
};

export function ManagerPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('upload');
  /**
   * The dataset a "New version" click is about. Kept on the page, not in the wizard: the
   * click happens in another panel, and the wizard is unmounted while it does.
   */
  const [pendingVersionOf, setPendingVersionOf] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /** ←/→/Home/End move between buttons; the focused button is the selected one. */
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const i = TABS.indexOf(tab);
      let next = i;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % TABS.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + TABS.length) % TABS.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = TABS.length - 1;
      else return;
      e.preventDefault();
      setTab(TABS[next]!);
      listRef.current
        ?.querySelector<HTMLButtonElement>(`[data-testid="mgr-tab-${TABS[next]}"]`)
        ?.focus();
    },
    [tab],
  );

  return (
    <div className="view mgr-view" data-testid="manager-page">
      <div className="mgr-wrap">
        <header className="mgr-head">
          <div className="mgr-title">
            <h2>{t('manager.title')}</h2>
            <p className="mgr-sub">{t('manager.sub')}</p>
          </div>
        </header>

        <div className="mgr-body">
        <div
          className="mgr-rail"
          role="tablist"
          aria-orientation="vertical"
          aria-label={t('manager.title')}
          ref={listRef}
          onKeyDown={onKeyDown}
        >
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              id={`mgrTab-${name}`}
              className={tab === name ? 'mgr-rail-btn on' : 'mgr-rail-btn'}
              aria-selected={tab === name}
              aria-controls="mgrPanel"
              tabIndex={tab === name ? 0 : -1}
              data-testid={`mgr-tab-${name}`}
              onClick={() => setTab(name)}
            >
              <span className="mgr-rail-in" data-testid={`mgr-rail-${name}`}>
                <span className="mgr-rail-ico">
                  <Icon name={TAB_ICON[name]} size={18} />
                </span>
                <span className="mgr-rail-text">
                  <strong className="mgr-rail-label">{t(`manager.tabs.${name}`)}</strong>
                  <span className="mgr-rail-hint">{t(`manager.railHint.${name}`)}</span>
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* `tabIndex={0}`: the panel is the only thing ←/→ can land the reader on, and the
            upload panel scrolls, so it has to be reachable from the rail by Tab. */}
        <div
          className="mgr-panel"
          id="mgrPanel"
          role="tabpanel"
          tabIndex={0}
          aria-labelledby={`mgrTab-${tab}`}
        >
          {tab === 'upload' ? (
            <UploadWizard
              mode="new"
              onSubmitted={() => {
                setTab('queue');
              }}
            />
          ) : null}
          {tab === 'replace' ? (
            <UploadWizard
              // Keyed on the preset so a second "New version" click restarts the wizard on
              // the new dataset rather than leaving it on the first one's state.
              key={pendingVersionOf ?? 'replace'}
              mode="replace"
              presetDatasetId={pendingVersionOf}
              onSubmitted={() => {
                setPendingVersionOf(null);
                setTab('queue');
              }}
            />
          ) : null}
          {tab === 'edit' ? <EditDataPanel /> : null}
          {tab === 'metadata' ? <MetadataPanel /> : null}
          {tab === 'values' ? (
            <ValuesPanel
              onSubmitted={() => {
                setTab('queue');
              }}
            />
          ) : null}
          {tab === 'queue' ? <Queue /> : null}
          {tab === 'datasets' ? (
            <DatasetsTab
              onNewVersion={(datasetId) => {
                setPendingVersionOf(datasetId);
                setTab('replace');
              }}
            />
          ) : null}
          {tab === 'intel' ? <IntelSettings /> : null}
        </div>
        </div>
      </div>
    </div>
  );
}

export default ManagerPage;
